import deepmerge from "deepmerge";
import * as _ from "lodash";
import { flow, observable } from "mobx";
import * as QueryString from "qs";
import {
  unsavedChanges,
  unsavedChangesDialog,
} from "../business-components/common/unsavedChanges";
import {
  PostMessageCVPRedirectHost,
  PostMessageTypes,
} from "../constants/PostMessageTypes";
import {
  getPathBindings,
  replaceCvpageParameterWithPathBinding,
} from "../settings/pathBinding";
import { settings } from "../settings/settings";
import { lp } from "../utils/logPrefix";
import { removeUndefined } from "../utils/util";
import { evaluatePath, getCurrentPath } from "./getCurrentPath";
import {
  CVPSearchParams,
  keepOnlyParamsThatBelongToPath,
  minimizeQueryStringValues,
  queryStringStore,
} from "./wsQueryStringStore";
import { routerStore } from "./WSRouter";

export interface IGetRedirectPropsResponse {
  href?: string;
  target?: "_blank" | undefined;
  onClick?: (event: any) => any;
}

export type IGetRedirectProps = (
  url: string,
  options: IRedirectOptions
) => IGetRedirectPropsResponse;

export const getRedirectProps: IGetRedirectProps = (url, options) => {
  url = completeUrl(url);

  options = {
    method: "push",
    mergeQs: null,
    ...removeUndefined(options),
  };

  const currentMainViewUrl = removeAppPrefix(
    routerStore.location.pathname + routerStore.location.search
  );

  let targetUrl = url;

  if (targetUrl === null) targetUrl = currentMainViewUrl;
  targetUrl = removeAppPrefix(targetUrl);

  if (window.cvpEmbedType) {
    targetUrl = adjustMainViewTargetUrlForEmbed(targetUrl, options).url;
  } else {
    targetUrl = addQueryStringToUrl(targetUrl, null, options);
  }

  return {
    // include href in addition to onclick to enable browser functionality (open in new window etc)
    href: targetUrl,
    target: options.target,
    onClick: (_event) => {
      const event = _event?.originalEvent ?? _event;

      if (event) {
        if (event.persist) event.persist(); // just a precaution
        if (event.preventDefault) event.preventDefault(); // prevent "normal" redirect
      }

      let _options = options;

      if (event?.ctrlKey) {
        _options = {
          ..._options,
          target: "_blank",
        };
      }

      redirect(url, _options);

      if (_options.onClick) {
        return _options.onClick(event);
      }

      // prevent form submit in old cvp
      return false;
    },
  };
};

const completeUrl = (to?: string) => {
  if ((to || "").indexOf(window.location.origin) === 0) {
    to = to.substring(window.location.origin.length);
  }

  return window.location.origin + to;
};

export interface ILocationState {
  [x: string]: any;
}

export interface IRedirectOptions {
  method?: "push" | "replace";
  onClick?: (evt) => any;
  target?: "_blank";
  clearLeftView?: boolean;
  clearSplitView?: boolean;

  state?: ILocationState;

  /**
   * true:
   * merge current and targetUrl querystrings
   *
   * object:
   * overwrite current querystring with given values, a null value removes the keyvalue pair completely
   */
  mergeQs: QueryString.ParsedQs | null;

  mergeState?: ILocationState;
  scrollToY?: number;
}

const redirectCounter = observable.box(0);

export enum RedirectResult {
  ok = "ok",
  unsavedChanges = "unsavedChanges",
}

export const redirect = flow(function* (
  targetUrl: string,
  options: IRedirectOptions
) {
  redirectCounter.set(redirectCounter.get() + 1);

  options = {
    method: "push",
    mergeQs: null,
    ...removeUndefined(options),
  };
  const currentState: any = routerStore.location.state || {};

  let state: any = options.state || {};

  /////////////////

  if (typeof options.mergeState === "object") {
    state = deepmerge(currentState, options.mergeState);
  }

  /////////////////

  //TODO form url so that hash comes after search

  if (targetUrl === null) targetUrl = getCurrentPath();
  targetUrl = removeAppPrefix(targetUrl);

  let hardRedirect = false;

  if (window.cvpEmbedType) {
    const currentCvpage = getCurrentPath();

    const adjusted = adjustMainViewTargetUrlForEmbed(targetUrl, options);
    targetUrl = adjusted.url;

    function needsHardRedirect() {
      if (!adjusted.usedPathBinding) {
        return false;
      }

      if (currentCvpage === adjusted.cvpage) {
        return false;
      }

      const pathBindings = getPathBindings(
        settings.get(),
        !!window.cvpSandboxType
      );

      const currentCvPageFirstPart = currentCvpage.split("/")[0];
      const adjustedCvPageFirstPart = adjusted.cvpage.split("/")[0];

      const groupOfCurrentCvpage = Object.values(pathBindings).find((group) =>
        group.pages.includes(currentCvPageFirstPart)
      );

      const groupOfNewCvpage = Object.values(pathBindings).find((group) =>
        group.pages.includes(adjustedCvPageFirstPart)
      );

      if (groupOfCurrentCvpage?.key === groupOfNewCvpage?.key) {
        return false;
      }

      return true;
    }

    hardRedirect = needsHardRedirect();
  } else {
    targetUrl = addQueryStringToUrl(targetUrl, null, options);
  }

  if (options.target) {
    if (targetUrl[0] === "/") {
      targetUrl = window.location.origin + targetUrl;
    }

    window.open(targetUrl, options.target);
    return RedirectResult.ok;
  }

  for (let i = 0; i < unsavedChanges.get().length; i++) {
    let allow = unsavedChangesDialog();

    if (allow && (allow as any).then) allow = yield allow;

    if (!allow) return RedirectResult.unsavedChanges;
  }

  let scrollToY = options.scrollToY;
  if (scrollToY === undefined) {
    scrollToY = shouldScrollToTopWhenNavigating(targetUrl) ? 0 : undefined;
  }

  if (window.cvpEmbedType === "iframe") {
    const origin = new URL(routerStore.hostUrl).origin;

    const msg: PostMessageCVPRedirectHost = {
      type: PostMessageTypes.CVPRedirectHost,
      url: targetUrl,
      method: options.method,
      scrollToY,
      hardRedirect: hardRedirect,
    };

    console.log(lp("window.parent.postMessage"), msg);
    window.parent.postMessage(msg, origin);
  } else {
    if (options.method === "push") {
      routerStore.history.push(targetUrl, state);
    } else if (options.method === "replace") {
      routerStore.history.replace(targetUrl, state);
    }

    if (scrollToY) {
      window.scrollTo({ behavior: "instant", top: scrollToY });
    }
  }

  return RedirectResult.ok;
});

function shouldScrollToTopWhenNavigating(targetUrl: string) {
  const currentPath = getCurrentPath();
  const newPath = evaluatePath(targetUrl);

  return currentPath !== newPath;
}

const removeAppPrefix = (url: string) => {
  if ((url || "").indexOf(window.location.origin) === 0) {
    url = url.substring(window.location.origin.length);
  }

  return url;
};

const addQueryStringToUrl = (
  targetUrl: string,
  embeddingUrl: string | null,
  options: IRedirectOptions
) => {
  if (!options.mergeQs) return targetUrl;

  // merge current and target querystrings

  const hasProtocol =
    targetUrl.startsWith("http://") || targetUrl.startsWith("https://");

  if (!hasProtocol && !targetUrl.startsWith("/")) {
    targetUrl = "/" + targetUrl;
  }

  const _targetUrl = new URL(
    hasProtocol ? targetUrl : "tempprotocol://" + targetUrl
  );

  let targetUrlQs = [..._targetUrl.searchParams.entries()].reduce(
    (acc, [key, value]) => {
      acc[key] = value;
      return acc;
    },
    {}
  );

  const currentQs = queryStringStore.qs;

  const targetPath = embeddingUrl || _targetUrl.pathname;

  // filter current querystring so we dont keep irrelevant querystring parameters
  const keepQs = keepOnlyParamsThatBelongToPath(targetPath, currentQs);
  targetUrlQs = keepOnlyParamsThatBelongToPath(targetPath, targetUrlQs);

  const qs = {
    ...keepQs,
    ...targetUrlQs,
    ...removeUndefined(options.mergeQs),
  };

  Object.entries(options.mergeQs).forEach(([key, value]) => {
    if (value === null) {
      delete qs[key];
    }
  });

  _targetUrl.search = new URLSearchParams(
    minimizeQueryStringValues(qs)
  ).toString();

  return _targetUrl.toString().replace("tempprotocol://", "");
};

const adjustMainViewTargetUrlForEmbed = (
  targetUrl: string,
  options: IRedirectOptions
) => {
  let [_url, _params] = targetUrl.split("?");

  let iframe_startPage = settings.get().iframe_startPage;

  if (_url.endsWith("/")) _url = _url.slice(0, _url.length - 1);
  if (_url.startsWith("/")) _url = _url.slice(1);

  if (iframe_startPage.endsWith("/"))
    iframe_startPage = iframe_startPage.slice(0, _url.length - 1);
  if (iframe_startPage.startsWith("/"))
    iframe_startPage = iframe_startPage.slice(1);

  const pathBindings = getPathBindings(settings.get(), !!window.cvpSandboxType);
  const hasPathBindings = Object.keys(pathBindings).length > 0;

  if (_url === iframe_startPage && !hasPathBindings) _url = null; // null means delete

  _url = _url || null;

  targetUrl = addQueryStringToUrl(
    routerStore.hostUrl ?? window.location.href,
    _url,
    {
      ...options,
      mergeQs: {
        ...options.mergeQs,
        [CVPSearchParams.cvpage]: _url,
      },
    }
  );

  const { didReplace, url, cvpage } = replaceCvpageParameterWithPathBinding(
    settings.get(),
    targetUrl,
    !!window.cvpSandboxType
  );

  return { url, usedPathBinding: didReplace, cvpage };
};
